// table-draft-surveys-component/table-draft-surveys-component.tsx
import React from 'react';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { SurveyCreationDraftTableDto } from '../../../../../typescript-axios/models/survey-creation-draft-table-dto';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { MarsinetContext } from '../../../../../context/marsinetProvider';
import { formatUtcTimestamp } from '../../../../../utils/timezone-utils';

interface Props {
    drafts: SurveyCreationDraftTableDto[];
    loading: boolean;
    onEditDraft: (draft: SurveyCreationDraftTableDto) => void;
    onDeleteDraft: (draft: SurveyCreationDraftTableDto) => void;
}

const TableDraftSurveysComponent: React.FC<Props> = ({
    drafts,
    loading,
    onEditDraft,
    onDeleteDraft,
}) => {
    const { t, i18n } = useTranslation();
    const { selectedTimezone } = useContext(MarsinetContext);

    const confirmDelete = (draft: SurveyCreationDraftTableDto) => {
        confirmDialog({
            message: t('pages.surveys.confirm_delete_draft_message'),
            header: t('pages.surveys.confirm_delete_draft_header'),
            icon: 'pi pi-trash',
            acceptClassName: 'p-button-danger',
            acceptLabel: t('yes'),
            rejectLabel: t('no'),
            accept: () => onDeleteDraft(draft),
        });
    };

    return (
        <DataTable
            value={drafts}
            loading={loading}
            selectionMode="single"
            onRowClick={(e) => onEditDraft(e.data as SurveyCreationDraftTableDto)}
            rows={10}
            emptyMessage={t('pages.surveys.no_drafts')}
            rowsPerPageOptions={[5, 10, 25, 50]}
            size="normal"
            removableSort
            style={{ paddingBottom: '1rem' }}
        >
            <Column
                header={t('pages.surveys.name')}
                sortable
                field="name"
                body={(rowData: SurveyCreationDraftTableDto) => (
                    <span style={{ fontSize: '1rem' }}>
                        {rowData.name?.trim() || (
                            <span className="text-color-secondary font-italic">
                                {t('pages.surveys.untitled_draft')}
                            </span>
                        )}
                    </span>
                )}
            />
            <Column
                header={t('pages.surveys.created_by')}
                sortable
                field="created_by"
                body={(rowData: SurveyCreationDraftTableDto) => (
                    <span style={{ fontSize: '1rem' }}>{rowData.created_by}</span>
                )}
            />
            <Column
                header={t('pages.surveys.last_edited')}
                sortable
                field="updated_at"
                body={(rowData: SurveyCreationDraftTableDto) => (
                    <span style={{ fontSize: '1rem' }}>
                        {/* Las fechas de edición de borradores son instantes UTC. */}
                        {formatUtcTimestamp(
                            rowData.updated_at,
                            selectedTimezone,
                            i18n.language
                        )}
                    </span>
                )}
            />
            <Column
                header={t('pages.devices.table.actions')}
                style={{ width: '8rem' }}
                body={(rowData: SurveyCreationDraftTableDto) => (
                    <div className="flex gap-1">
                        <Button
                            type="button"
                            icon="pi pi-trash"
                            className="p-button-rounded p-button-danger p-button-text p-button-sm"
                            tooltip={t('pages.surveys.delete_draft')}
                            tooltipOptions={{ position: 'bottom' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                confirmDelete(rowData);
                            }}
                        />
                    </div>
                )}
            />
        </DataTable>
    );
};

export default TableDraftSurveysComponent;